 import {BaseModuleState} from "../../../../../infrastructure/models/base-module-state";
import {DynamicPagesLinkModel} from "../../../../../containers/dynamic-pages-container/models/dynamic-pages.link-model";
import {computed, observable, makeObservable, runInAction, override} from "mobx";
import {BaseMobxState} from "../../../../../infrastructure/mobx/base-mobx-state";
import {NavigationManager} from "../../../../../core/managers/navigation-manager";
import {AppStore} from "../../../../../core/app/app-store";
import {LoggerUtils} from "../../../../../infrastructure/utils/logger/logger-utils";
import {DatetimeUtils} from "../../../../../infrastructure/utils/datetime-utils";
import {DeviceViewModel} from "../../../../../core/models/device.view-model";
import {TermsUtils} from "../../../../../infrastructure/utils/terms-utils";
import {FooterModuleConfig} from "../footer-module.config";
import {SocialIconsViewModel} from "../../../../../core/components/social-icons/models/social-icons.view-model";
import {FooterModuleLinkModel} from "./footer-module.link-model";
import {FOOTER_MODULE} from "../footer-module.consts";
import {DYNAMIC_PAGES_CONTAINER} from "../../../../../containers/dynamic-pages-container/dynamic-pages.container.consts";
import {FOOTER_MODULE_REMOTE_CONFIG} from "../footer-module.remote-config";
import {AllScoresLinkParams} from "../../../../../containers/all-scores-container/models/all-scores-container-link-params";
import {PAGES, ROUTE_NAMES} from "../../../../../core/consts";
import {ARGS} from "../../../../../core/bootstrap/args";
import {RemoteConfigManager} from "../../../../../third-party-services/remote-config/remote-config-manager";
import {FooterModuleDynamicLinksModel} from "./footer-module.dynamic-links.model";
import {FooterModuleDynamicLinkModel} from "./footer-module.dynamic-link.model";
import {BaseLinkModel} from "../../../../../infrastructure/models/link/base-link-model";
 import {WEBSITE_MODULE} from "../../../website-module.consts";
 import {FooterModuleCountryComplianceModel} from "./footer-module.country-compliance.model";
 import {LegalImageRequestParams} from "./legal.image.request-params";
 import {ProhibitionImageRequestParams} from "./prohibition.image.request-params";

export class FooterModuleState extends BaseModuleState {
  @observable description: String;
  @observable dynamicLinksConfiguration: FooterModuleDynamicLinksModel;

  static injectables = [...BaseMobxState.injectables, NavigationManager, AppStore, LoggerUtils, DatetimeUtils, DeviceViewModel, TermsUtils, FooterModuleConfig, RemoteConfigManager];

  constructor(navigationManager: NavigationManager, appStore: AppStore, logger: LoggerUtils, datetimeUtils: DatetimeUtils, device: DeviceViewModel, termsUtils: TermsUtils, config: FooterModuleConfig, remoteConfigManager: RemoteConfigManager) {
    super(navigationManager, appStore, logger, datetimeUtils, device, termsUtils, config);

    this.dynamicLinksConfiguration = this.config.getValue(FOOTER_MODULE_REMOTE_CONFIG.FOOTER_DYNAMIC_LINKS);

    makeObservable(this);

    remoteConfigManager.registerToInitCallback(() => runInAction(() => {
      this.dynamicLinksConfiguration = this.config.getValue(FOOTER_MODULE_REMOTE_CONFIG.FOOTER_DYNAMIC_LINKS);
    }))
  }

  get dynamicPages(): Array<FooterModuleLinkModel> {
    const aboutLink: FooterModuleLinkModel = new FooterModuleLinkModel(FOOTER_MODULE.ANALYTICS.TYPES.about, this.terms.FOOTER_ABOUT, null, new DynamicPagesLinkModel(DYNAMIC_PAGES_CONTAINER.ALIAS_NAMES.about), false, 1, true);
    const privacyLink: FooterModuleLinkModel = new FooterModuleLinkModel(FOOTER_MODULE.ANALYTICS.TYPES.privacy, this.terms.FOOTER_PRIVACY, null, new DynamicPagesLinkModel(DYNAMIC_PAGES_CONTAINER.ALIAS_NAMES.privacy), false, 2);
    const publishersLink: FooterModuleLinkModel = this.createExternalLink(FOOTER_MODULE.ANALYTICS.TYPES.publishers, this.terms.ABOUT_PUBLISHERS_FOOTER, 'https://www.publishers.365scores.com', 3, false, true);
    const advertiseLink: FooterModuleLinkModel = new FooterModuleLinkModel(FOOTER_MODULE.ANALYTICS.TYPES.advertise, this.terms.FOOTER_ADVERTIZE, null, new DynamicPagesLinkModel(DYNAMIC_PAGES_CONTAINER.ALIAS_NAMES.advertise), false, 4);
    const contactLink: FooterModuleLinkModel = new FooterModuleLinkModel(FOOTER_MODULE.ANALYTICS.TYPES.contact, this.terms.FOOTER_CONTACT, null, new DynamicPagesLinkModel(DYNAMIC_PAGES_CONTAINER.ALIAS_NAMES.contact), false, 5);
    // const deleteAccountLink: FooterModuleLinkModel = new FooterModuleLinkModel(FOOTER_MODULE.ANALYTICS.TYPES.deleteAccount, this.terms.FOOTER_DELETE_ACCOUNT, null, new DynamicPagesLinkModel(DYNAMIC_PAGES_CONTAINER.ALIAS_NAMES.deleteAccount), false, 6);
    const termsLink: FooterModuleLinkModel = new FooterModuleLinkModel(FOOTER_MODULE.ANALYTICS.TYPES.terms, this.terms.FOOTER_TERMS, null, new DynamicPagesLinkModel(DYNAMIC_PAGES_CONTAINER.ALIAS_NAMES.terms), false, 7);
    const dynamicPagesLinks: Array<FooterModuleLinkModel> = [aboutLink, privacyLink, publishersLink, advertiseLink, contactLink, termsLink, this.jobsLink];

    return dynamicPagesLinks.sort((a, b) => a.position - b.position);
  }

  @override get currentLinkModel(): BaseLinkModel {
    return this.navigationManager.currentLinkModel;
  }

  @computed get jobsLink(): BaseLinkModel {
    let jobsLink: FooterModuleLinkModel;

    if (this.appState.locale.userCountryId === 6 || this.appState.locale.language.id === 2) {
      jobsLink = this.createExternalLink(FOOTER_MODULE.ANALYTICS.TYPES.jobs, this.terms.FOOTER_JOBS, this.config.getValue(FOOTER_MODULE_REMOTE_CONFIG.FOOTER_ISRAEL_JOBS), 7, false, true);
    } else {
      jobsLink = new FooterModuleLinkModel(FOOTER_MODULE.ANALYTICS.TYPES.jobs, this.terms.FOOTER_JOBS, null, new DynamicPagesLinkModel(DYNAMIC_PAGES_CONTAINER.ALIAS_NAMES.jobs), false, 7);
    }

    return jobsLink;
  }

  @computed get dynamicLinks(): Array<FooterModuleDynamicLinkModel> {
    let dynamicLinks: Array<FooterModuleDynamicLinkModel> = new FooterModuleDynamicLinksModel(
      this.dynamicLinksConfiguration, this.appState.language, this.sportTypeId, this.userCountryId, this.terms, this.currentLinkModel)
      .links?.filter((link: FooterModuleDynamicLinkModel): Boolean => {
        const isVisible: Boolean = link.visible;

        if(this.isBettingPage) {
          return isVisible && !link.isScoresLink
        } else {
          return isVisible && !link.isBettingZoneLink;
        }
      });

    return dynamicLinks || [];
  }

  @computed get allLinks(): Array<FooterModuleLinkModel> {
    let allLinks: Array<FooterModuleLinkModel> = [...this.dynamicPages, ...this.dynamicLinks];

    return allLinks;
  }

  createExternalLink(label: String, term: String, url: String, position: Number, forceFollow: Boolean = false, forceDisplay = false): FooterModuleLinkModel {
    let externalLink: FooterModuleLinkModel = null;

    const countries = FOOTER_MODULE.EXTERNAL_LINKS[label]?.countries;
    const languages = FOOTER_MODULE.EXTERNAL_LINKS[label]?.languages;
    const hasLang: Boolean = languages?.includes(this.language.id);
    const hasCountry: Boolean = countries?.includes(this.appState.locale.userCountryId);
    const shouldCreateLink: Boolean = hasLang || hasCountry

    if (shouldCreateLink || forceDisplay) {
      externalLink = new FooterModuleLinkModel(label, term, url, null, false, position, forceFollow);
    }

    return externalLink
  }

  get majorLinks(): Array<FooterModuleLinkModel> {
    const majorLinks: Array<FooterModuleLinkModel> = this.allLinks.filter((link: FooterModuleLinkModel) => link.isMajor);

    return majorLinks;
  }

  get nonMajorLinks(): Array<FooterModuleLinkModel> {
    const regularLinks: Array<FooterModuleLinkModel> = this.allLinks.filter((link: FooterModuleLinkModel) => !link.isMajor);

    return regularLinks;
  }

  @computed get userCountryId(): Number {
    return this.appState.locale?.userCountryId;
  }

  @computed get sportTypeId(): Number {
    return this.appState.context.sportType?.id;
  }

  @computed get isHomePage() {
    const params = new AllScoresLinkParams(this.currentLinkModel?.params.sportName, this.currentLinkModel?.params.filter);
    const isAllScoresRoute = this.currentLinkModel?.name === ROUTE_NAMES.allScores;
    const isHomePage: Boolean = !params.sportName && isAllScoresRoute;

    return isHomePage;
  }

  get appStoreUrl(): String {
    return 'https://itunes.apple.com/us/app/365scores/id571801488?mt=8';
  }

  get googlePlayUrl(): String {
    return 'https://play.google.com/store/apps/details?id=com.scores365&hl=en';
  }

  get versionLabel(): String {
    const {VERSION_NUMBER} = ARGS;

    return `${this.terms.VERSION_NO_KEY}: ${VERSION_NUMBER}`;
  }

  get socialLinks(): SocialIconsViewModel {
    return new SocialIconsViewModel(this.appState.language.cultureName)
  }

  get isPopulated() {
    return true;
  }

  get copyrightText() {
    const currentYear: Date = new Date().getFullYear();

    return `${FOOTER_MODULE.COPYRIGHT_TEXTS.copyRight}${currentYear} ${FOOTER_MODULE.COPYRIGHT_TEXTS.allRightReserved}`
  }

  @computed get isBottomNavBarDisplay() {
    const validRoutes = [
      ROUTE_NAMES.allScores,
      ROUTE_NAMES.myScoresPage
    ];

    return validRoutes.includes(this.currentLinkModel?.name)
  }

  @computed get shouldRenderDisclaimer() {
    return this.appState.topBookmaker?.id > 0;
  }

  @computed get isBettingPage(): Boolean {
    const bettingPages: Array<ROUTE_NAMES> = [ROUTE_NAMES.betting, ...(Object.values(PAGES.BETTING))];

    return bettingPages.includes(this.currentLinkModel?.name);
  }

  @computed get shouldRenderLangMenu() {
    return this.device.isSmallView && !this.isBettingPage;
  }

  @computed get shouldRenderExpandLangMenu() {
    return !this.device.isSmallView && !this.isBettingPage
  }

  @computed get disclaimerId() {
    const validLanguagesIds = [1,14,29,31];

    return validLanguagesIds.includes(this.appState.language?.id) ? this.appState.language.id : WEBSITE_MODULE.DEFAULT_LANGUAGE_ID;
  }

  @computed get shouldRenderBettingDisclosure(): Boolean {
    return this.isBettingPage && !!this.terms.WEB_FOOTER_BETTING_DISCLOSURE;
  }

  @computed get isAdVisible(): Boolean {
    const externalAdsModel = this.appState?.externalAdsModel;

    return !!externalAdsModel?.shouldRender && externalAdsModel?.displayLayout > 0;
  }

  get bettingDisclosureText(): String {
    return this.terms.WEB_FOOTER_BETTING_DISCLOSURE;
  }

  get bettingAffDisclosureHeader(): String {
    return this.terms.BZ_FOOTER_AFF_DISCLOSURE;
  }

  get bettingGambleResponsibleHeader(): String {
    return this.terms.BZ_FOOTER_GAMBLE_RESPONSIABLE;
  }

  get countryCompliance() {
    const prohibitionImageSize: Number = this.device.isSmallView ? 40 : 48;

    const map = {
      2: [
        new FooterModuleCountryComplianceModel("https://www.ordenacionjuego.es/participantes-juego/juego-seguro", new LegalImageRequestParams('logo_juego-seguro_1')),
        new FooterModuleCountryComplianceModel("https://www.ordenacionjuego.es/participantes-juego/juego-autorizado", new LegalImageRequestParams('logo-juego-autorizado_1')),
        new FooterModuleCountryComplianceModel("https://www.ordenacionjuego.es/participantes-juego/juego-seguro/rgiaj", new ProhibitionImageRequestParams(prohibitionImageSize, prohibitionImageSize), prohibitionImageSize),
      ]
    }

    return map[this.appState.locale?.userCountryId] || null
  }
}
